import React from 'react';

import {
  regulationFor,
  regulationRules,
  TModalityRulesDraft,
  TModalityRulesErrors,
} from '../../utils/tournament/modalityRules';
import MinSecInput from './MinSecInput';
import {
  ErrorText,
  Field,
  Footer,
  Group,
  GroupTitle,
  Hint,
  NumberBox,
  ResetButton,
  Row,
  Sentence,
  Unit,
  Wrapper,
} from './styles';

interface IModalityRulesFieldsProps {
  /** Slug da modalidade (robo_pintor | seguidor_de_linha). */
  modality: string;
  value: TModalityRulesDraft;
  onChange: (next: TModalityRulesDraft) => void;
  /** Erros já calculados pelo pai com validateModalityRules. */
  errors?: TModalityRulesErrors;
  disabled?: boolean;
  /** O cronograma edita rodadas em outro lugar. */
  showRounds?: boolean;
}

const toInt = (raw: string): number | null => {
  const digits = raw.replace(/\D/g, '');
  return digits === '' ? null : Number(digits);
};

/**
 * Duração da rodada, número de rodadas e bônus de tempo de uma modalidade,
 * com o regulamento como padrão. Usado na criação, na edição do torneio e no
 * cronograma, para os três lugares editarem o mesmo do mesmo jeito.
 */
const ModalityRulesFields: React.FC<IModalityRulesFieldsProps> = ({
  modality,
  value,
  onChange,
  errors = {},
  disabled = false,
  showRounds = true,
}) => {
  const regulation = regulationFor(modality);
  const defaults = regulationRules(modality);
  const set = (patch: Partial<TModalityRulesDraft>) =>
    onChange({ ...value, ...patch });

  const differsFromRegulation =
    value.time_limit_seconds !== defaults.time_limit_seconds ||
    value.bonus_time_seconds !== defaults.bonus_time_seconds ||
    value.bonus_points !== defaults.bonus_points ||
    (showRounds && value.max_rounds !== defaults.max_rounds);

  return (
    <Wrapper>
      <Group>
        <GroupTitle>Rodada</GroupTitle>
        <Row>
          <Field as="div">
            Duração
            <MinSecInput
              label="Duração da rodada"
              seconds={value.time_limit_seconds}
              onChange={time_limit_seconds => set({ time_limit_seconds })}
              hasError={!!errors.time_limit_seconds}
              disabled={disabled}
            />
          </Field>
          {showRounds && (
            <Field>
              Rodadas por equipe
              <NumberBox
                type="text"
                inputMode="numeric"
                $hasError={!!errors.max_rounds}
                disabled={disabled}
                value={value.max_rounds ?? ''}
                onChange={e => set({ max_rounds: toInt(e.target.value) })}
              />
            </Field>
          )}
        </Row>
        {errors.time_limit_seconds && (
          <ErrorText role="alert">{errors.time_limit_seconds}</ErrorText>
        )}
        {errors.max_rounds && (
          <ErrorText role="alert">{errors.max_rounds}</ErrorText>
        )}
      </Group>

      <Group>
        <GroupTitle>Bônus de tempo</GroupTitle>
        <Sentence>
          Concluiu as 5 missões em menos de
          <MinSecInput
            label="Tempo do bônus"
            seconds={value.bonus_time_seconds}
            onChange={bonus_time_seconds => set({ bonus_time_seconds })}
            hasError={!!errors.bonus_time_seconds}
            disabled={disabled}
          />
          <Unit>e ganha</Unit>
          <NumberBox
            type="text"
            inputMode="numeric"
            aria-label="Pontos do bônus"
            $hasError={!!errors.bonus_points}
            disabled={disabled}
            value={value.bonus_points ?? ''}
            onChange={e => set({ bonus_points: toInt(e.target.value) })}
          />
          <Unit>pts</Unit>
        </Sentence>
        {errors.bonus_time_seconds && (
          <ErrorText role="alert">{errors.bonus_time_seconds}</ErrorText>
        )}
        {errors.bonus_points && (
          <ErrorText role="alert">{errors.bonus_points}</ErrorText>
        )}
      </Group>

      <Footer>
        <Hint>{regulation.summary}</Hint>
        {differsFromRegulation && (
          <ResetButton
            type="button"
            disabled={disabled}
            onClick={() =>
              onChange(
                showRounds
                  ? { ...defaults }
                  : { ...defaults, max_rounds: value.max_rounds },
              )
            }
          >
            Restaurar padrão do regulamento
          </ResetButton>
        )}
      </Footer>
    </Wrapper>
  );
};

export default ModalityRulesFields;
